import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { orderBy, trim, uniqBy } from 'lodash';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from 'src/auth/hooks';
import DataTable from 'src/components/DataTable';
import { useAlert } from 'src/components/alert';
import Breadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { UserNotesTooltip } from 'src/components/users/user-notes-tooltip';
import QuickTextDialog from 'src/sections/manager/manual-text-messages/quick-text-dialog';
import { useOrganizationContext } from 'src/context/organization';
import { GET_ORGANIZATION_BY_ID } from 'src/graphql/organizations/queries';
import { DEACTIVATE_USER, GET_ALL_USERS, GET_ORG_USER_NOTES } from 'src/graphql/users/queries';
import { useDownloadCSV } from 'src/hooks/useDownloadCSV';
import { useParams, useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { MultipleOrgUsersModal } from 'src/sections/manager/users/components/multi-org-users-modal';
import InviteVolunteersDialog from 'src/sections/manager/users/components/invite-volunteers-dialog';
import RowAction from 'src/sections/manager/users/components/row-action';
import TableHeader from 'src/sections/manager/users/components/table-header';
import UsersKpis from 'src/sections/manager/users/components/users-kpis';
import {
  DEFAULT_USER_FILTERS,
  filterUsers,
  orgMembership,
} from 'src/sections/manager/users/users-kpi-model';
import { fDate } from 'src/utils/format-time';
import { resolveEffectiveRole, roleDisplayLabel } from 'src/utils/roles';

const MESSAGE_ALL_ROLE_DEFS = [
  { code: 'superadmin', label: 'Super Admins' },
  { code: 'manager', label: 'Managers' },
  { code: 'legislative_member', label: 'Legislative Members' },
  { code: 'volunteer', label: 'Volunteers' },
];

const normalizeRoleCode = (roleCode) =>
  ['superadmin', 'manager', 'legislative_member'].includes(roleCode) ? roleCode : 'volunteer';

/**
 * The role label for a user within the organization being viewed. Shared by the Role column and
 * the user export so the two can never disagree about a user's role.
 */
const orgRoleLabel = (row, organizationId) =>
  roleDisplayLabel(
    orgMembership(row, organizationId)?.user_organizations_role?.code,
    row?.users_user_roles?.[0]?.user_roles_roles?.code
  );

const teamsForOrg = (row, organizationId) =>
  orderBy(
    row?.users_user_groups?.filter((g) => g?.user_groups_group?.organization_id === organizationId),
    (g) => g?.user_groups_group?.name
  );

export default function UserManagementPage() {
  const router = useRouter();
  const { selectedOrganizationId, selectedOrgData } = useOrganizationContext();
  const { orgId } = useParams();
  const alert = useAlert();
  const { downloadResultsCSV } = useDownloadCSV();

  const { user } = useAuthContext();
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState();
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageAllOpen, setMessageAllOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_USER_FILTERS);
  const userRole = user?.users_user_roles?.[0]?.user_roles_roles?.code;
  const isSuperAdmin = userRole === 'superadmin';

  /**
   * This page serves two routes: /users, where a manager's organization comes from context, and
   * /superadmin/organizations/:orgId/users, where a superadmin names one in the URL. The
   * precedence works for both because a manager never has the param and a superadmin's
   * selectedOrganizationId is 0 (context/organization.js). Same resolution as the create page
   * this one links to, pages/users/user-create.js.
   *
   * `isOrgScoped`, NOT `isSuperAdmin`, is what the layout turns on. The question every branch
   * below is really asking is "is this list about one organization", and a superadmin viewing
   * :orgId/users is — they should get the manager's screen, tiles and all. `isSuperAdmin` is
   * kept only where it means "this viewer has powers across organizations".
   */
  const organizationId = selectedOrganizationId || (orgId ? parseInt(orgId, 10) : 0);
  const isOrgScoped = !!organizationId;
  const isFromOrgRoute = !!orgId && !selectedOrganizationId;

  const {
    data: usersData,
    loading,
    refetch,
  } = useQuery(GET_ALL_USERS, {
    variables: {
      filter: {
        ...(isOrgScoped
          ? {
              users_user_organizations: {
                organization_id: {
                  _eq: organizationId,
                },
                flg_inactive: {
                  _eq: 'N',
                },
              },
            }
          : {}),
      },
    },
  });

  const { data: orgData } = useQuery(GET_ORGANIZATION_BY_ID, {
    variables: { org_id: organizationId },
    skip: !isFromOrgRoute,
  });

  const { data: orgUserNotesData } = useQuery(GET_ORG_USER_NOTES, {
    variables: {
      where: {
        ...(isOrgScoped ? { organization_id: { _eq: organizationId } } : {}),
      },
    },
    skip: !isOrgScoped && !isSuperAdmin,
  });

  const [deActivateUser] = useMutation(DEACTIVATE_USER);

  const onCreateUser = () => {
    // pages/users/user-create reads its organization from :orgId when context has none, so the
    // superadmin route has to keep the id in the path or they land on an org-less form.
    router.push(
      isFromOrgRoute ? `/superadmin/organizations/${orgId}/users/create` : paths.users.create
    );
  };

  const onEditUser = (userId, orgId) => {
    router.push(
      paths.users.edit.link({
        orgId: orgId,
        userId: userId,
      })
    );
  };

  const onDeleteUser = (userObj) => {
    const userId = userObj?.user_id;
    let orgId = '';

    const userOrganizations = userObj?.users_user_organizations;
    if (!isOrgScoped && userOrganizations?.length > 1) {
      // Only the cross-organization list has to ask. Scoped to one organization, the answer is
      // that organization -- taking [0] there could remove them from a different one entirely.
      setSelectedUserForDeletion(userObj);
      return;
    }

    orgId = isOrgScoped
      ? organizationId
      : userOrganizations?.[0]?.user_organizations_organization?.organization_id;
    alert.confirm('Are you sure you want to delete this user?').then((confirm) => {
      if (confirm) {
        markUserAsInactive(userId, [orgId]);
      }
    });
  };

  const markUserAsInactive = (userId, orgIds) => {
    deActivateUser({
      variables: {
        user_id: userId,
        org_ids: orgIds,
      },
    }).finally(() => {
      refetch();
    });
  };

  const users = usersData?.rows?.map((row) => ({
    name: `${row?.first_name} ${row?.last_name}`,
    ...row,
  }));

  /**
   * One filtered array behind the KPI tiles, the table and the export, so nobody can be shown a
   * count that the rows in front of them contradict. The cross-organization superadmin list has
   * no single membership to filter on, so it is passed through untouched and offered no strip.
   */
  const filteredUsers = useMemo(
    () => (isOrgScoped ? filterUsers(users ?? [], { organizationId, ...filters }) : users),
    [users, isOrgScoped, organizationId, filters]
  );

  const onExportUsers = () => {
    const csvData = orderBy(filteredUsers, 'name').map((u) => ({
      'First Name': u?.first_name ?? '',
      'Last Name': u?.last_name ?? '',
      Email: u?.email ?? '',
      Phone: u?.phone ?? '',
      Role: orgRoleLabel(u, organizationId),
      Teams: teamsForOrg(u, organizationId)
        .map((t) => t?.user_groups_group?.name)
        .filter(Boolean)
        .join('; '),
      Address: u?.address ?? '',
      'Address 2': u?.address2 ?? '',
      City: u?.city ?? '',
      State: u?.state ?? '',
      Zip: u?.zip ?? '',
    }));

    const filenameParts = ['Users'];
    const orgName = selectedOrgData?.name || orgData?.row?.name;
    if (orgName) filenameParts.push(orgName.replace(/\s+/g, '_'));
    filenameParts.push(format(new Date(), 'yyyy-MM-dd'));

    downloadResultsCSV(filenameParts.join('_'), csvData);
  };

  /**
   * Grouped by the role the Role column shows: the one in the organization being viewed, falling
   * back to the global role on the cross-organization list (which has no membership to read).
   * Scoped to one organization, groups nobody belongs to (Super Admins, usually) are left out.
   */
  const messageAllRoleGroups = MESSAGE_ALL_ROLE_DEFS.map(({ code, label }) => {
    const groupUsers = (users || []).filter(
      (u) =>
        normalizeRoleCode(
          resolveEffectiveRole(
            u?.users_user_roles?.[0]?.user_roles_roles?.code,
            orgMembership(u, organizationId)?.user_organizations_role?.code
          )
        ) === code
    );
    const phones = groupUsers.map((u) => u?.phone).filter(Boolean);
    const emails = groupUsers.map((u) => u?.email).filter(Boolean);
    return {
      code,
      label,
      phones,
      emails,
      count: groupUsers.length,
      missingCount: groupUsers.length - phones.length,
    };
  }).filter((g) => !isOrgScoped || g.count > 0);

  const columns = [
    {
      dataField: 'name',
      text: 'Name',
      searchable: true,
      formatter: (cell, row) => {
        return (
          <Link
            onClick={() => {
              router.push(paths.users.profile.link({ userId: row?.user_id }));
            }}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="subtitle2">{`${row?.first_name} ${row?.last_name}`}</Typography>
          </Link>
        );
      },
    },
    {
      /** this is just a placeholder column to let the email be searchable */
      dataField: 'email',
      searchable: true,
      formatter: (cell, row) => {
        return null;
      },
    },
    ...(isOrgScoped
      ? []
      : [
          {
            dataField: 'organization_id',
            text: 'No. of Organizations',
            searchable: false,
            formatter: (cell, row) => {
              return uniqBy(
                row?.users_user_organizations,
                (item) => item?.user_organizations_organization?.organization_id
              )?.length;
            },
          },
        ]),
    {
      dataField: 'users_user_roles',
      searchable: false,
      text: 'Role',
      formatter: (cell, row) => (
        <Chip label={orgRoleLabel(row, organizationId)} variant="outlined" size="small" />
      ),
    },

    ...(isOrgScoped
      ? [
          {
            dataField: 'users_user_groups',
            text: 'Teams',
            formatter: (cell, row) => {
              const teams = teamsForOrg(row, organizationId);

              return (
                <Stack direction={'row'} spacing={1} flexWrap="wrap" maxWidth={250}>
                  {teams?.map((team) => (
                    <Chip
                      key={team?.user_groups_group?.group_id}
                      label={team?.user_groups_group?.name}
                      variant="filled"
                      size="small"
                    />
                  ))}
                </Stack>
              );
            },
          },
          {
            dataField: 'notes',
            text: 'Notes',
            formatter: (cell, row) => {
              const notesData = orgUserNotesData?.rows?.find(
                (note) => note.user_id === row.user_id
              );
              const notes = trim(notesData?.notes);

              return notes ? <UserNotesTooltip notesData={notesData} /> : null;
            },
          },
        ]
      : []),

    {
      dataField: 'phone#message',
      text: 'Message User',
      searchable: false,
      align: 'center',
      formatter: (cell, row) => {
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<Iconify icon="solar:chat-round-dots-bold" />}
            onClick={() =>
              setMessageTarget({
                recipientLabel: `${row?.first_name} ${row?.last_name}`,
                numbers: [row?.phone].filter(Boolean),
                emails: [row?.email].filter(Boolean),
              })
            }
          >
            <div style={{ marginLeft: -8 }} />
          </Button>
        );
      },
    },
    {
      dataField: 'user_id#actions',
      text: 'Action',
      searchable: false,
      align: 'center',
      formatter: (cell, row) => {
        return (
          <RowAction
            row={row}
            // The "Edit user for: <organization>" picker is for the cross-organization list.
            // Scoped to one organization there is nothing to pick.
            isSuperAdmin={!isOrgScoped}
            hideDelete={row?.user_id === user?.user_id}
            onEdit={(pickedOrgId) =>
              onEditUser(row?.user_id, isOrgScoped ? organizationId : pickedOrgId)
            }
            onDelete={() => onDeleteUser(row)}
          />
        );
      },
    },
  ];

  return (
    <Container>
      <Helmet>
        <title>Users</title>
      </Helmet>
      <Breadcrumbs
        heading="Users"
        sx={{ mb: { xs: 3, md: 5 } }}
        links={
          isFromOrgRoute
            ? [
                { name: 'Organizations Overview', href: '/superadmin/organizations' },
                { name: orgData?.row?.name, href: `/superadmin/organizations/${orgId}/edit` },
                { name: 'Users' },
              ]
            : [{ name: 'User Management' }, { name: 'Users' }]
        }
      />
      <Card>
        {isOrgScoped ? (
          <UsersKpis
            users={filteredUsers ?? []}
            allUsers={users ?? []}
            organizationId={organizationId}
            filters={filters}
            onChange={setFilters}
            loading={loading}
          />
        ) : null}
        <TableHeader
          onCreateUser={onCreateUser}
          count={filteredUsers?.length}
          onMessageAll={isSuperAdmin || isOrgScoped ? () => setMessageAllOpen(true) : undefined}
          onExportUsers={isOrgScoped ? onExportUsers : undefined}
          // A list spanning every organization has no single one to invite to.
          onInviteVolunteers={isOrgScoped ? () => setInviteOpen(true) : undefined}
          userCount={filteredUsers?.length ?? 0}
          loading={loading}
        />
        <DataTable
          search
          searchBarProps={{
            sx: {
              p: 1,
              //m: 1,
            },
            fullWidth: true,
            size: 'medium',
            placeholder: 'Search Users...',
          }}
          size="small"
          keyField="user_id"
          loading={loading}
          pagination
          data={orderBy(filteredUsers, 'name')}
          columns={columns}
          renderChildData={
            !isOrgScoped
              ? (row) => {
                  const userOrganizations = orderBy(
                    uniqBy(
                      row?.users_user_organizations,
                      (item) => item?.user_organizations_organization?.organization_id,
                      row
                    ),
                    (item) => item?.user_organizations_organization?.name
                  );

                  return (
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <Typography component="span" variant="subtitle2">
                            Email:{' '}
                          </Typography>
                          {row?.email || '--'}
                        </Typography>
                        <Typography variant="body2">
                          <Typography component="span" variant="subtitle2">
                            Phone:{' '}
                          </Typography>
                          {row?.phone || '--'}
                        </Typography>
                      </Stack>
                      <Table size="small">
                        <TableHead>
                          <TableCell>Organization</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell>Teams</TableCell>
                        </TableHead>
                        <TableBody>
                          {userOrganizations?.map((userOrg) => {
                            const notesData = orgUserNotesData?.rows?.find(
                              (note) =>
                                note.user_id === row.user_id &&
                                note.organization_id ===
                                  userOrg?.user_organizations_organization?.organization_id
                            );

                            const teams = row?.users_user_groups?.filter(
                              (group) =>
                                group?.user_groups_group?.organization_id ===
                                userOrg?.user_organizations_organization?.organization_id
                            );
                            return (
                              <TableRow
                                key={userOrg?.user_organizations_organization?.organization_id}
                              >
                                <TableCell>
                                  {userOrg?.user_organizations_organization?.name}
                                </TableCell>
                                <TableCell>
                                  {userOrg?.user_organizations_role?.code === 'owner'
                                    ? 'Manager'
                                    : 'Volunteer'}
                                </TableCell>
                                <TableCell>
                                  {trim(notesData?.notes) && (
                                    <UserNotesTooltip notesData={notesData} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack
                                    direction={'row'}
                                    spacing={1}
                                    flexWrap="wrap"
                                    maxWidth={250}
                                  >
                                    {teams?.map((team) => (
                                      <Chip
                                        key={team?.user_groups_group?.group_id}
                                        label={team?.user_groups_group?.name}
                                        variant="filled"
                                        size="small"
                                      />
                                    ))}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                }
              : (row) => {
                  const notesData = orgUserNotesData?.rows?.find(
                    (note) => note.user_id === row.user_id
                  );

                  const notes = trim(notesData?.notes);
                  const notesDate = fDate(notesData?.updated_at);
                  const notesUpdatedBy = `${notesData?.org_user_notes_last_updated_by?.first_name} ${notesData?.org_user_notes_last_updated_by?.last_name}`;

                  return (
                    <Box sx={{ p: 2 }}>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '50%' }}>Details</TableCell>
                              <TableCell sx={{ width: '50%' }}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Email</TableCell>
                              <TableCell>{row?.email || '--'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Phone</TableCell>
                              <TableCell>{row?.phone || '--'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Notes</TableCell>
                              <TableCell>
                                <Stack>
                                  <Typography variant="body2">{notes || '--'}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {notes && notesUpdatedBy
                                      ? `${notesUpdatedBy} | ${notesDate}`
                                      : ''}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                }
          }
        />
      </Card>
      {selectedUserForDeletion?.user_id && (
        <MultipleOrgUsersModal
          open
          user={selectedUserForDeletion}
          onClose={() => setSelectedUserForDeletion(undefined)}
          onDeleteUsers={markUserAsInactive}
        />
      )}
      {messageTarget ? (
        <QuickTextDialog open onClose={() => setMessageTarget(null)} {...messageTarget} />
      ) : null}
      {messageAllOpen ? (
        <QuickTextDialog
          open
          onClose={() => setMessageAllOpen(false)}
          recipientLabel="All Users"
          roleGroups={messageAllRoleGroups}
        />
      ) : null}
      {inviteOpen ? (
        <InviteVolunteersDialog
          open
          onClose={() => setInviteOpen(false)}
          organizationId={organizationId}
        />
      ) : null}
    </Container>
  );
}
